import ThumbDownAlt from "@mui/icons-material/ThumbDownAlt";
import ThumbDownOffAlt from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpAlt from "@mui/icons-material/ThumbUpAlt";
import ThumbUpOffAlt from "@mui/icons-material/ThumbUpOffAlt";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { useCallback, useRef, useState, type MouseEvent } from "react";
import {
  nextFeedbackAfterReactionClick,
  type FeedbackState,
  type Reaction,
} from "@/shared/lib/feedbackReaction";
import {
  FeedbackCommentPopover,
  type FeedbackCommentSubmitResult,
  type FeedbackLabels,
} from "@/shared/ui/feedback/FeedbackCommentPopover";

export type {
  FeedbackCommentSubmitResult,
  FeedbackLabels,
} from "@/shared/ui/feedback/FeedbackCommentPopover";

type FeedbackThumbsProps = {
  feedback?: FeedbackState | null;
  labels: FeedbackLabels;
  /** Fire-and-forget from the caller's perspective, but must resolve/reject so the
   * double-click guard below can release once the underlying request actually settles. */
  onReact: (reaction: Reaction) => Promise<unknown>;
  onSubmitComment: (comment: string) => Promise<FeedbackCommentSubmitResult>;
};

type CommentTarget = {
  anchorEl: HTMLElement;
  reaction: Reaction;
};

/** Thumbs up/down + the optional comment popover it opens. Purely prop-driven — no Redux, no
 * i18n — so it can back both nugget feedback and chat turn feedback from one implementation. */
export function FeedbackThumbs({
  feedback,
  labels,
  onReact,
  onSubmitComment,
}: FeedbackThumbsProps) {
  // Anchor and reaction are set together so the popover's wording can never disagree with
  // the thumb that opened it.
  const [commentTarget, setCommentTarget] = useState<CommentTarget | null>(null);
  const reactionInFlightRef = useRef(false);

  const currentReaction = feedback?.reaction ?? null;

  const handleReactionClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, reaction: Reaction) => {
      // The host is commonly itself a clickable card/row — without this, reacting would also
      // trigger whatever that host does on click.
      event.preventDefault();
      event.stopPropagation();

      // A fast double click would otherwise fire two requests that both read "no reaction yet"
      // and race to insert, tripping the unique constraint. React state isn't synchronous
      // enough to block that, hence the ref.
      if (reactionInFlightRef.current) {
        return;
      }
      reactionInFlightRef.current = true;

      const anchor = event.currentTarget;
      // Fire and forget: the reaction is recorded regardless of what happens to the popover.
      void onReact(reaction).finally(() => {
        reactionInFlightRef.current = false;
      });

      // This click may have cleared the reaction rather than set one. Don't prompt for a
      // comment then — there is no longer a feedback row to attach it to, so submitting
      // would 404.
      const next = nextFeedbackAfterReactionClick(feedback, reaction);
      setCommentTarget(next.reaction === null ? null : { anchorEl: anchor, reaction });
    },
    [feedback, onReact],
  );

  const handleCommentClose = useCallback(() => {
    setCommentTarget(null);
  }, []);

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title={labels.thumbUpTooltip}>
          <IconButton
            size="small"
            aria-label={labels.thumbUpAriaLabel}
            aria-pressed={currentReaction === "UP"}
            onClick={(event) => handleReactionClick(event, "UP")}
            sx={{
              color:
                currentReaction === "UP"
                  ? "var(--theme-primary-light)"
                  : "var(--theme-text-secondary)",
              "&:hover": { color: "var(--theme-primary-light)" },
            }}
          >
            {currentReaction === "UP" ? (
              <ThumbUpAlt fontSize="small" />
            ) : (
              <ThumbUpOffAlt fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title={labels.thumbDownTooltip}>
          <IconButton
            size="small"
            aria-label={labels.thumbDownAriaLabel}
            aria-pressed={currentReaction === "DOWN"}
            onClick={(event) => handleReactionClick(event, "DOWN")}
            sx={{
              color:
                currentReaction === "DOWN"
                  ? "var(--theme-primary-light)"
                  : "var(--theme-text-secondary)",
              "&:hover": { color: "var(--theme-primary-light)" },
            }}
          >
            {currentReaction === "DOWN" ? (
              <ThumbDownAlt fontSize="small" />
            ) : (
              <ThumbDownOffAlt fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>

      <FeedbackCommentPopover
        anchorEl={commentTarget?.anchorEl ?? null}
        reaction={commentTarget?.reaction ?? null}
        existingComment={feedback?.comment ?? null}
        labels={labels}
        onSubmit={onSubmitComment}
        onClose={handleCommentClose}
      />
    </>
  );
}
